/*
 *  Copyright (c) 2022-2023 Nordcloud Oy or its affiliates. All Rights Reserved.
 */

import { useMemo } from "react";
import { ParentSize } from "@visx/responsive";
import { useTooltipInPortal, useTooltip } from "@visx/tooltip";
import { When } from "react-if";
import { BrickLoader } from "@nordcloud/gnui";
import { UniversalWrap } from "~/components";
import {
  CostTableData,
  getColorsPalette,
  getTableColumns,
  StackBarChart,
} from "~/components/Charts/CostAnalysis";
import { tooltipStyles } from "~/components/Charts/CostAnalysis/components";
import {
  generateLinkParameters,
  getEndDate,
  mapStackBarChartColors,
} from "~/components/Charts/CostAnalysis/helpers";
import {
  ChartType,
  Granularity,
  LinkQueryParameterName,
  Period,
} from "~/components/Charts/CostAnalysis/types";
import { useCurrency } from "~/hooks";
import { useOrgUnitCostPerCategoriesChart } from "../hooks/useOrgUnitCostPerCategoriesChart";

type Props = {
  orgUnitId: string;
  startDate: string;
  endDate: string;
  granularity: Granularity;
  period: Period;
  hasFooter: boolean;
  showCostTable: boolean;
  applicationIds?: string[];
};

export function OrgUnitCostPerCategoriesView({
  orgUnitId,
  startDate,
  endDate,
  granularity,
  period,
  hasFooter,
  showCostTable,
  applicationIds,
}: Props) {
  const { currency } = useCurrency();

  const {
    orgUnitTopCategoriesCostChartData,
    costPerCategoryTableData,
    costPerCategoryTotal,
    isLoading,
    error,
    getSubRows,
    organizationUnits,
    otherCategoryIds,
  } = useOrgUnitCostPerCategoriesChart({
    orgUnitId,
    startDate,
    endDate: getEndDate(endDate),
    period,
    granularity,
  });

  const linkParameters = useMemo(
    () =>
      generateLinkParameters(
        LinkQueryParameterName.APPLICATION,
        applicationIds ?? [],
        otherCategoryIds
      ),
    [applicationIds, otherCategoryIds]
  );

  const columns = useMemo(
    () =>
      getTableColumns({
        startDate,
        endDate,
        currency,
        data: costPerCategoryTableData,
        hasFooter,
        period,
        chartType: ChartType.COST_PER_CATEGORY,
        colorsPalette: getColorsPalette(ChartType.COST_PER_CATEGORY),
        linkParameters,
      }),
    [
      startDate,
      endDate,
      currency,
      hasFooter,
      costPerCategoryTableData,
      linkParameters,
      period,
    ]
  );

  const {
    showTooltip,
    hideTooltip,
    tooltipOpen,
    tooltipData,
    tooltipLeft,
    tooltipTop,
  } = useTooltip();

  const { containerRef, TooltipInPortal } = useTooltipInPortal({
    detectBounds: true,
    scroll: true,
  });

  return (
    <UniversalWrap
      loaderProps={{ loading: isLoading, Component: BrickLoader }}
      errorProps={{ error }}
    >
      <ParentSize>
        {(parent) => (
          <>
            <StackBarChart
              width={parent.width}
              height={280}
              data={orgUnitTopCategoriesCostChartData}
              granularity={granularity}
              loading={isLoading}
              startDate={startDate}
              endDate={endDate}
              period={period}
              colors={mapStackBarChartColors(organizationUnits)}
              showTooltip={showTooltip}
              hideTooltip={hideTooltip}
              keys={organizationUnits}
              containerRef={containerRef}
            />
            <When condition={tooltipOpen}>
              <TooltipInPortal
                key={Date.now()}
                top={tooltipTop}
                left={tooltipLeft}
                style={tooltipStyles}
              >
                {tooltipData}
              </TooltipInPortal>
            </When>
          </>
        )}
      </ParentSize>
      <CostTableData
        period={period}
        startDate={startDate}
        endDate={endDate}
        tableData={costPerCategoryTableData}
        chartType={ChartType.COST_PER_CATEGORY}
        total={costPerCategoryTotal}
        tableError={error}
        columns={columns}
        isLoading={isLoading}
        showCostTable={showCostTable}
        hasFooter={hasFooter}
        linkParameters={linkParameters}
        getSubRows={getSubRows}
      />
    </UniversalWrap>
  );
}
